/**
 * La conversación con la mascota: los globos, las tarjetas de «¿lo creo?» y la
 * caja para escribir. La misma pieza en las dos caras del asistente —el globo
 * de Inicio y la mascota flotante—, que comparten conversación (ver
 * `dashboardAssistant.ts`).
 */
import { useEffect, useLayoutEffect, useRef, useState, type FormEvent, type KeyboardEvent, type ReactNode } from 'react'
import { IconArrowRight, IconClock, IconArrowUp, IconCheck, IconFileText, IconLayoutGrid, IconLoader2, IconTag } from '@tabler/icons-react'
import { AI_THINK_MS, AiMascot, AiThinkingLabel, AiTypingText, ProfileAvatarContent, useAiReplyPhase } from '@webbuilder/ui'
import { useI18n } from '../../lib/i18n'
import {
  askAssistant,
  cancelProposal,
  confirmProposal,
  offerKindLabel,
  openAssistantTarget,
  useAssistant,
  type AssistantMessage,
  type AssistantProposal,
} from '../../lib/dashboardAssistant'
import { AssistantAiMeter, nextReset } from './AssistantAiMeter'
import { AiOutCard } from './AiOutCard'

export function AssistantChat({ variant, autoFocus = false, maxMessages, lastTurnOnly = false, olderAction, toolbarEnd, userName, avatarUrl, footnoteAction }: {
  /**
   * Sólo la última pregunta con su respuesta (Inicio). Lo anterior no se pinta:
   * Inicio no puede crecer con cada vuelta ni tener una caja con scroll dentro.
   */
  lastTurnOnly?: boolean
  /** Lo que va encima cuando hay vueltas anteriores escondidas. */
  olderAction?: ReactNode
  /** A la derecha de esa fila, con la charla empezada: «Nueva conversación». */
  toolbarEnd?: ReactNode
  /** Lo que va junto al aviso de abajo («Empezar de nuevo» en Inicio). */
  footnoteAction?: ReactNode
  userName?: string | undefined
  avatarUrl?: string | null | undefined
  variant: 'inline' | 'floating'
  autoFocus?: boolean
  /** En Inicio sólo lo último: el encabezado no puede crecer sin fin. */
  maxMessages?: number
}) {
  const { t } = useI18n()
  const { messages, busy, busyLabel, budget } = useAssistant()
  const [draft, setDraft] = useState('')
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus()
  }, [autoFocus])

  const hasLog = messages.length > 0
  /* Lo nuevo a la vista: la lista tiene su propio scroll, y crece también
     mientras una respuesta se escribe, no sólo al llegar mensajes. */
  useEffect(() => {
    const list = listRef.current
    if (!list) return
    list.scrollTop = list.scrollHeight
    const observer = new MutationObserver(() => { list.scrollTop = list.scrollHeight })
    observer.observe(list, { childList: true, subtree: true, characterData: true })
    return () => observer.disconnect()
  }, [hasLog, busy])

  const send = (text: string) => {
    if (!text.trim() || busy) return
    setDraft('')
    void askAssistant(text)
  }

  const onSubmit = (event: FormEvent) => {
    event.preventDefault()
    send(draft)
  }

  /* Enter envía y Mayús+Enter salta de renglón, como en cualquier chat. Sin
     enviar a mitad de una composición (acentos con teclado japonés, etc.). */
  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent.isComposing) return
    event.preventDefault()
    send(draft)
  }

  /* La caja crece con lo escrito hasta un tope, y luego hace scroll. */
  useLayoutEffect(() => {
    const box = inputRef.current
    if (!box) return
    box.style.height = 'auto'
    box.style.height = `${Math.min(box.scrollHeight, variant === 'inline' ? 200 : 140)}px`
  }, [draft, variant])

  const lastUser = messages.map((message) => message.role).lastIndexOf('user')
  const shown = lastTurnOnly && lastUser > 0
    ? messages.slice(lastUser)
    : maxMessages ? messages.slice(-maxMessages) : messages
  const hiddenOlder = lastTurnOnly && lastUser > 0
  const examples = ['assistant.example.find', 'assistant.example.offer', 'assistant.example.post']

  return (
    <div className={`dash-assist dash-assist--${variant}`}>
      {shown.length || busy || variant === 'floating' ? (
        <div className="dash-assist-log" ref={listRef} role="log" aria-live="polite" aria-label={t('assistant.title')}>
          {variant === 'floating' && !shown.length && !busy ? (
            <div className="dash-assist-empty">
              <div className="dash-assist-message">
                <span className="dash-assist-avatar is-mascot" aria-hidden="true"><AiMascot still /></span>
                <p className="dash-assist-bubble is-assistant">{t('assistant.hello')}</p>
              </div>
              <div className="dash-assist-chips">
                {examples.map((key) => (
                  <button key={key} type="button" className="dash-assist-chip is-example" onClick={() => send(t(key))}>
                    {t(key)}
                  </button>
                ))}
              </div>
            </div>
          ) : null}
          {/* Arriba de la conversación, donde se busca: lo anterior a la
              izquierda y empezar otra a la derecha. */}
          {messages.length && ((hiddenOlder && olderAction) || toolbarEnd) ? (
            <div className="dash-assist-toolbar">
              {hiddenOlder ? olderAction : <span />}
              {toolbarEnd}
            </div>
          ) : null}
          {shown.map((message) => <Message key={message.id} message={message} userName={userName} avatarUrl={avatarUrl} />)}
          {busy && !messages.some((message) => message.proposalState === 'running') ? (
            <ThinkingRow label={t(busyLabel ?? 'assistant.thinking')} />
          ) : null}
        </div>
      ) : null}

      {/* Al 80 %, una línea antes de que se acabe. */}
      {budget && !budget.exhausted && budget.limit !== null && budget.percentUsed >= 80 ? (
        <p className="dash-assist-low">{t('ai.low', { pct: Math.max(0, 100 - Math.round(budget.percentUsed)) })}</p>
      ) : null}
      {/* Agotada, no hay caja: escribir para que luego te digan que no se
          siente como un fallo. */}
      {budget?.exhausted ? (
        <AiOutCard
          title={t('ai.out.title')}
          body={t('ai.out.body', { date: nextReset(t).date, when: nextReset(t).when })}
          onOpenPlans={() => openAssistantTarget({ kind: 'global', id: 'plans' })}
        />
      ) : (
      <form className="dash-assist-form" onSubmit={onSubmit} onClick={() => inputRef.current?.focus()}>
        <textarea
          ref={inputRef}
          className="dash-assist-input"
          rows={1}
          value={draft}
          maxLength={600}
          placeholder={t('assistant.placeholder')}
          aria-label={t('assistant.placeholder')}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={onKeyDown}
        />
        <div className="dash-assist-form-foot">
          {/* Lo que queda de IA este mes, como en el editor. */}
          {budget ? <AssistantAiMeter budget={budget} onOpenPlans={() => openAssistantTarget({ kind: 'global', id: 'plans' })} /> : null}
          <button className="dash-assist-send" type="submit" disabled={busy || !draft.trim()} aria-label={t('assistant.send')}>
            {busy ? <IconLoader2 size={16} className="dash-assist-spin" aria-hidden="true" /> : <IconArrowUp size={16} aria-hidden="true" />}
          </button>
        </div>
      </form>
      )}
      {/* Lo de siempre en un chat con IA, y sólo con la charla empezada: antes
          de escribir nada sería ruido. */}
      {messages.length ? (
        <p className="dash-assist-footnote">
          {t('assistant.disclaimer')}
          {footnoteAction ? <>{' · '}{footnoteAction}</> : null}
        </p>
      ) : null}
      {/* En Inicio, con la charla vacía, ideas de qué pedir bajo la caja. */}
      {variant === 'inline' && !messages.length && !busy && !budget?.exhausted ? (
        <div className="dash-assist-chips dash-assist-suggestions">
          {examples.map((key) => (
            <button key={key} type="button" className="dash-assist-chip is-example" onClick={() => send(t(key))}>
              {t(key)}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  )
}

/** Las respuestas que ya terminaron de escribirse: al repintarse (de Inicio a
    la flotante, por ejemplo) salen enteras y no se vuelven a teclear. */
const revealed = new Set<string>()

/** La mascota girando y «Pensando…»: lo mismo que se ve en el editor. */
function ThinkingRow({ label }: { label: string }) {
  return (
    <div className="dash-assist-row is-assistant">
      <div className="dash-assist-message">
        <span className="dash-assist-avatar is-mascot" aria-hidden="true"><AiMascot working /></span>
        <p className="dash-assist-bubble is-assistant"><AiThinkingLabel label={label} /></p>
      </div>
    </div>
  )
}

function Message({ message, userName, avatarUrl }: { message: AssistantMessage; userName?: string | undefined; avatarUrl?: string | null | undefined }) {
  const { t, fmt } = useI18n()
  /* Sólo la recién llegada se anima: piensa un momento —aunque la respuesta
     ya esté—, se escribe, y entonces salen los botones. Ver `AiReply`. */
  const [animate] = useState(() => message.role === 'assistant' && !revealed.has(message.id) && Date.now() - (message.createdAt ?? 0) < 1500)
  const [phase, finish] = useAiReplyPhase(animate, message.thinkMs ?? AI_THINK_MS)
  useEffect(() => {
    if (phase === 'done') revealed.add(message.id)
  }, [phase, message.id])

  if (phase === 'thinking') return <ThinkingRow label={t('assistant.thinking')} />

  return (
    <div className={`dash-assist-row is-${message.role}`}>
      {/* La mascota a la izquierda y quien escribe a la derecha, cada uno con
          su cara: así no hace falta pintar dos globos de colores. */}
      <div className="dash-assist-message">
        {message.role === 'assistant' ? (
          <span className="dash-assist-avatar is-mascot" aria-hidden="true"><AiMascot still /></span>
        ) : null}
        <p className={`dash-assist-bubble is-${message.role}${message.failed ? ' is-failed' : ''}`}>
          {message.role === 'assistant' ? <AiTypingText text={message.text} active={phase === 'typing'} onDone={finish} /> : message.text}
        </p>
        {message.role === 'user' ? (
          <span className="dash-assist-avatar" aria-hidden="true">
            <ProfileAvatarContent name={userName} src={avatarUrl} />
          </span>
        ) : null}
      </div>
      {/* Sólo admins: el servidor no la manda a nadie más. */}
      {phase === 'done' && message.usage ? (
        <p className="dash-assist-usage">
          {message.usage.cached
            ? t('assistant.usageCached')
            : t('assistant.usage', { input: fmt.number(message.usage.input), output: fmt.number(message.usage.output) })
              + (message.usage.thought ? ' · ' + t('assistant.usageThought', { thought: fmt.number(message.usage.thought) }) : '')}
        </p>
      ) : null}
      {phase === 'done' && message.chips?.length ? (
        <div className={`dash-assist-chips${animate ? ' cwa-ai-reveal' : ''}`}>
          {message.chips.map((chip, index) => (
            <button key={`${chip.label}:${index}`} type="button" className="dash-primary-btn dash-assist-btn dash-assist-go" onClick={() => openAssistantTarget(chip.target)}>
              <span>{chip.label}</span>
              {chip.detail ? <span className="dash-assist-chip-detail">{chip.detail}</span> : null}
              <IconArrowRight size={12} aria-hidden="true" />
            </button>
          ))}
        </div>
      ) : null}
      {phase === 'done' && message.proposal ? (
        <div className={animate ? 'cwa-ai-reveal' : undefined}>
          <ProposalCard id={message.id} proposal={message.proposal} state={message.proposalState} />
        </div>
      ) : null}
    </div>
  )
}

/** «lun–jue · 8:00–17:00», con los nombres del idioma de quien mira. */
function scheduleText(schedule: { days: number[]; startMinute: number; endMinute: number }, language: string): string {
  /* 7 de enero de 2024 fue domingo: el día 0. */
  const day = (weekday: number) => new Date(2024, 0, 7 + weekday).toLocaleDateString(language, { weekday: 'short' })
  const time = (minute: number) => new Date(2024, 0, 7, Math.floor(minute / 60), minute % 60).toLocaleTimeString(language, { hour: 'numeric', minute: '2-digit' })
  const days = [...schedule.days].sort()
  const consecutive = days.every((value, index) => index === 0 || value === days[index - 1]! + 1)
  const dayText = consecutive && days.length > 2 ? `${day(days[0]!)}–${day(days[days.length - 1]!)}` : days.map(day).join(', ')
  return `${dayText} · ${time(schedule.startMinute)}–${time(schedule.endMinute)}`
}

function ProposalCard({ id, proposal, state }: { id: string; proposal: AssistantProposal; state: AssistantMessage['proposalState'] }) {
  const { t, fmt, language } = useI18n()
  const { busy } = useAssistant()

  const head = proposal.kind === 'offer'
    ? { icon: <IconTag size={20} aria-hidden="true" />, kind: offerKindLabel(t, proposal.offerKind), title: proposal.title, note: t('assistant.offer.note'), action: t('assistant.offer.createOpen') }
    : proposal.kind === 'page'
      ? { icon: <IconLayoutGrid size={20} aria-hidden="true" />, kind: t('assistant.page.kind'), title: proposal.idea || t('assistant.page.untitled'), note: t('assistant.page.note'), action: t('assistant.page.open') }
      : { icon: <IconFileText size={20} aria-hidden="true" />, kind: t('assistant.post.kind'), title: proposal.title, note: t('assistant.post.note'), action: t('assistant.post.saveOpen') }

  const running = state === 'running'
  return (
    <div className={`dash-assist-card is-${proposal.kind} is-${state ?? 'pending'}`} aria-busy={running}>
      <div className="dash-assist-card-head">
        <span className="dash-assist-card-icon">{head.icon}</span>
        <span className="dash-assist-card-kind">{head.kind}</span>
        {proposal.kind === 'offer' && proposal.price !== undefined ? (
          <span className="dash-assist-card-price">{fmt.number(proposal.price)}</span>
        ) : null}
      </div>
      <div className="dash-assist-card-body">
        <p className="dash-assist-card-title">{head.title}</p>
        {/* Lo que ya está escrito, para decidir viéndolo. */}
        {proposal.kind === 'offer' && (proposal.shortDescription || proposal.description) ? (
          <p className="dash-assist-card-desc">{proposal.shortDescription || proposal.description}</p>
        ) : null}
        {/* Las variantes que se van a crear: «Color: Rojo», «Talla: L · XL». */}
      {proposal.kind === 'offer' && proposal.options?.length ? (
        <ul className="dash-assist-card-options">
          {proposal.options.map((option) => (
            <li key={option.name}>
              <span className="dash-assist-card-option-name">{option.name}</span>
              {option.values.map((value) => (
                <span key={value} className="dash-assist-card-option-value">
                  {option.swatches?.[value] ? <i style={{ background: option.swatches[value] }} aria-hidden="true" /> : null}
                  {value}
                </span>
              ))}
            </li>
          ))}
        </ul>
      ) : null}
      {/* El horario de la agenda que se va a guardar. */}
      {proposal.kind === 'offer' && proposal.schedule ? (
        <p className="dash-assist-card-schedule">
          <IconClock size={13} aria-hidden="true" />
          {scheduleText(proposal.schedule, language)}
          {proposal.scheduleReplaces ? <small>{t('assistant.offer.scheduleReplaces')}</small> : null}
        </p>
      ) : null}
      {proposal.kind === 'post' && proposal.excerpt ? <p className="dash-assist-card-desc">{proposal.excerpt}</p> : null}
        <p className="dash-assist-card-note">{head.note}</p>
      </div>
      {state === 'pending' || running ? (
        <div className="dash-assist-card-actions">
          <button type="button" className="dash-secondary-btn dash-assist-btn" disabled={running} onClick={() => cancelProposal(id)}>
            {t('assistant.cancel')}
          </button>
          <button type="button" className="dash-primary-btn dash-assist-btn" disabled={running || busy} onClick={() => { void confirmProposal(id) }}>
            {running ? <IconLoader2 size={14} className="dash-assist-spin" aria-hidden="true" /> : null}
            {head.action}
            {!running ? <IconArrowRight size={14} aria-hidden="true" /> : null}
          </button>
        </div>
      ) : state === 'done' ? (
        <p className="dash-assist-card-status"><IconCheck size={13} aria-hidden="true" /> {t('assistant.doneShort')}</p>
      ) : state === 'cancelled' ? (
        <p className="dash-assist-card-status is-muted">{t('assistant.cancelledShort')}</p>
      ) : null}
    </div>
  )
}
